import React, { useState, FormEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock as LockIcon } from '@mui/icons-material';
import { changePasswordDirect } from '@timetable/api';
import { validatePassword, getPasswordRequirementsText } from '@timetable/utils';

export const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const validate = () => {
    const result = validatePassword(newPassword, confirmPassword);
    if (!result.valid) {
      setPasswordError(result.errors.password || result.errors.confirmPassword || '');
      return false;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      await changePasswordDirect(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message ?? 'Failed to change password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">Change Password</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Update your password to keep your account secure.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password changed successfully!
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            Current Password
          </Typography>
          <TextField
            fullWidth
            size="small"
            type={showPasswords ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            placeholder="••••••••"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPasswords((s) => !s)}>
                    {showPasswords ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            New Password
          </Typography>
          <TextField
            fullWidth
            size="small"
            type={showPasswords ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (passwordError) setPasswordError('');
            }}
            required
            placeholder="••••••••"
            autoComplete="new-password"
            helperText={getPasswordRequirementsText()}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            Confirm New Password
          </Typography>
          <TextField
            fullWidth
            size="small"
            type={showPasswords ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (passwordError) setPasswordError('');
            }}
            required
            placeholder="••••••••"
            autoComplete="new-password"
            error={!!passwordError}
            helperText={passwordError}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
          >
            {submitting ? 'Changing Password...' : 'Change Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};